import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTicket } from '@/queries/tickets'
import { useTicketEvents } from '@/hooks/useTicketEvents'
import { MessageThread } from './MessageThread'
import { ContextPanel } from './ContextPanel'
import { RequesterDetails } from './RequesterDetails'
import { ChangeRequesterDialog } from '@/features/ticket-admin/ChangeRequesterDialog'
import { TicketFollowers } from '@/features/ticket-admin/TicketFollowers'
import { ReplyComposer } from './ReplyComposer'
import { CloseTicketButton } from './CloseTicketButton'
import { FullPageSpinner } from '@/components/etainabl-spinner'
import { InvestigationRailCard } from '@/features/ai-investigate/InvestigationRailCard'
import { DRAFT_LABEL, takeDraft } from '@/features/ai-investigate/draftHandoff'
import { ClaimBanner } from '@/features/agent-channel/ClaimBanner'
import { StoodDownNote } from '@/features/agent-channel/StoodDownNote'
import { ActivityTimeline } from '@/features/agent-channel/ActivityTimeline'
import { AgentItemCard } from '@/features/agent-channel/AgentItemCards'
import { hasAgentRelayActivity, useAgentPanel } from '@/queries/agent'
import { useAiSettings } from '@/queries/settings'
import type { AgentItem } from '@/types/agent'
import { isOwnerViewer } from '@/lib/scope'
import { formatTicketRef } from '@/lib/ticketRef'
import { statusPill } from '@/lib/status'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { widerThanShell } from '@/lib/layout'
import type { ReactNode } from 'react'

// The conversation shares the page with the staff rail, so AppShell's 1080px column left the thread cramped. Full-bleed
// below 1400px, as the pane always was.
const WIDE_PANE = widerThanShell(1400)

export function TicketDetailView({ ticketId, adminControls, asUser, showInvestigations }: {
  ticketId: string
  adminControls?: ReactNode
  asUser?: string
  showInvestigations?: boolean
}) {
  const { live } = useTicketEvents(ticketId, asUser)
  const { data, isLoading, error } = useTicket(ticketId, asUser, { poll: !live })
  const navigate = useNavigate()
  // Remounting the composer (via `key`) is how a fresh seed overwrites
  // whatever the user was already typing, instead of colliding with it.
  const [draftSeed, setDraftSeed] = useState<{ text: string; v: number; label?: string } | null>(null)
  // The agent's draft being edited in the composer, whose send then approves the edited text.
  const [editingDraft, setEditingDraft] = useState<AgentItem | null>(null)
  // Agent Relay's channel is staff-only, and never under the view-as lens, which shows the customer's view.
  const staffOnly = Boolean(adminControls) && !asUser
  const agent = useAgentPanel(ticketId, staffOnly)
  const aiSettings = useAiSettings(staffOnly && Boolean(showInvestigations))
  // A draft closed elsewhere while being edited (a public reply, an answer on Slack) ends the edit: sending would only
  // be refused.
  const editedItem = editingDraft && agent.data?.items.find(item => item._id === editingDraft._id)
  const editedItemClosed = Boolean(editedItem && editedItem.status !== 'pending')
  useEffect(() => {
    if (!editedItemClosed) return
    setEditingDraft(null)
    setDraftSeed(s => ({ text: '', v: (s?.v ?? 0) + 1 }))
  }, [editedItemClosed])
  // A draft handed over from the full investigation page lands in the composer once
  useEffect(() => {
    const text = takeDraft(ticketId)
    if (text) setDraftSeed(s => ({ text, v: (s?.v ?? 0) + 1, label: DRAFT_LABEL }))
  }, [ticketId])
  if (isLoading) return <FullPageSpinner />

  // A forged asUser on a non-admin session gets an explicit message, not a
  // blank "not found" that reads as a broken link.
  const status = (error as { status?: number; statusCode?: number } | null)?.status
    ?? (error as { statusCode?: number } | null)?.statusCode
  if (status === 403) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        You don&apos;t have access to view as another user.
      </div>
    )
  }

  if (error || !data) return <div className="p-8">Ticket not found.</div>

  // The rail is the staff workbench: property controls, the AI investigation,
  // the page context and the action trail. Only the admin route passes
  // adminControls, so its presence IS staff-ness — a customer's own ticket page
  // is the conversation and nothing else (approved mockup, "Conversation +
  // slim rail" / client view). Everything inside the rail also self-gates
  // (TrackerControls and ActionTrail both re-check isAdmin).
  const staffView = Boolean(adminControls)

  // Closing is the reporter's own call to make; a colleague or company admin
  // reading the same ticket doesn't get it, and neither does the read-only
  // view-as lens. A tracked issue is excluded because its status is derived
  // from the tracker — the API refuses that close for the same reason.
  const pill = statusPill(data.ticket, staffView ? 'staff' : 'customer')

  const canClose =
    !data.viewingAs &&
    isOwnerViewer(data.viewerRelation) &&
    data.ticket.status !== 'closed' &&
    !data.ticket.tracker

  // A customer's slim rail holds only the people on the ticket: shown to whoever
  // can manage them, or when there is someone besides the requester to list.
  // The view-as lens gets the list but never the controls.
  const canManageFollowers = Boolean(data.canManageFollowers) && !data.viewingAs
  const customerRail = !staffView && (canManageFollowers || (data.ticket.followerUsers?.length ?? 0) > 0)

  const agentPanel = staffOnly && !data.viewingAs ? agent.data : undefined
  const agentItems = agentPanel?.items ?? []
  // Cutover: the old Investigate panel steps aside once Agent Relay works this ticket, or once the help desk's own
  // agent is switched off (ai-settings.enabled), which is how a workspace moves to Agent Relay's Support agent.
  const showOldInvestigate = showInvestigations && aiSettings.data?.settings.enabled === true && !hasAgentRelayActivity(agentPanel)
  // The draft being edited as the panel's latest poll has it, so the edit carries the disclosure line the card shows now
  // (one filled in since Edit was pressed included, and refetched after a 409 'changed').
  const editingNow = editingDraft ? (agentItems.find(item => item.questionId === editingDraft.questionId) ?? editingDraft) : null
  const editDraft = (item: AgentItem) => {
    setEditingDraft(item)
    setDraftSeed(s => ({ text: item.text, v: (s?.v ?? 0) + 1 }))
  }
  const stopEditing = () => {
    setEditingDraft(null)
    setDraftSeed(s => ({ text: '', v: (s?.v ?? 0) + 1 }))
  }

  const conversation = (
    <>
      <header className="flex shrink-0 items-start gap-3.5 px-6 pb-1 pt-6">
        <span className="shrink-0 pt-1 text-etn-control font-semibold tabular-nums text-etn-text-soft">
          {formatTicketRef(data.ticket)}
        </span>
        <h1 className="min-w-0 flex-1 truncate text-[1.25rem] font-semibold leading-tight">{data.ticket.subject}</h1>
        <Badge data-testid="status-pill" variant={pill.variant} className="mt-1 shrink-0">{pill.label}</Badge>
      </header>
      {agentPanel?.claim && <ClaimBanner claim={agentPanel.claim} />}
      {agentPanel?.stoodDown && <StoodDownNote ticketId={ticketId} stoodDown={agentPanel.stoodDown} />}
      <div className="min-h-0 flex-1 overflow-y-auto px-6">
        <MessageThread
          ticketId={ticketId}
          messages={data.messages}
          staffViewerId={staffOnly ? data.me._id : undefined}
          openingMessageId={data.messages.find(m => m.kind === 'reply')?._id}
          extras={agentItems.map(item => ({
            key: `agent-${item._id}`,
            at: item.createdAt,
            node: <AgentItemCard ticketId={ticketId} item={item} all={agentItems} onEdit={editDraft} />,
          }))}
        />
      </div>
      <div className="shrink-0 border-t bg-background">
        {data.viewingAs ? (
          <div className="p-4 text-sm text-muted-foreground">
            This is a read-only view — replies are disabled while viewing as another user.
          </div>
        ) : (
          <>
            {canClose && (
              <div className="flex justify-end px-4 pt-3">
                <CloseTicketButton ticketId={ticketId} />
              </div>
            )}
            <ReplyComposer
              key={draftSeed?.v ?? 0}
              ticketId={ticketId}
              status={data.ticket.status}
              viewer={data.viewerRelation}
              initialBody={draftSeed?.text}
              draftLabel={draftSeed?.label}
              editDraft={
                editingNow ? { questionId: editingNow.questionId, disclosure: editingNow.disclosure ?? null, onDone: stopEditing } : undefined
              }
            />
          </>
        )}
      </div>
    </>
  )

  return (
    // Negative margins cancel AppShell main's padding (pt-7 pb-12) and widen the
    // pane past its 1080px column (WIDE_PANE), and its height is the viewport minus
    // AppShell's chrome (navbar and view-as bar), so the composer stays on screen
    // however long the thread gets. Both must track AppShell: a mismatch pushes the
    // composer below the fold.
    <div className="-mt-7 -mb-12 flex h-[calc(100dvh-var(--shell-chrome,3.5rem))] flex-col" style={WIDE_PANE}>
      <div className="flex min-h-0 flex-1">
        {/* One conversation, mounted once. Staff get it as the flexible left
            column with the rail beside it; a customer gets the same element
            centred at reading width. The two views differ in what is composed
            AROUND the conversation, never in the conversation itself. */}
        <div
          className={`flex min-w-0 flex-col overflow-hidden ${staffView ? 'flex-1' : 'mx-auto w-full max-w-[960px]'}`}
          data-testid="conversation"
        >
          {conversation}
        </div>
        {staffView && (
          <aside
            className="w-[clamp(292px,41%,440px)] shrink-0 overflow-y-auto border-l border-etn-border-hairline bg-background px-5 pb-10 pt-5"
            data-testid="ticket-rail"
          >
            {/* requester is only sent to staff outside the view-as lens, which
                is also the only place the people controls may write from. */}
            {data.requester && (
              <>
                <RequesterDetails
                  requester={data.requester}
                  action={
                    <ChangeRequesterDialog
                      ticket={data.ticket}
                      requester={data.requester}
                      trigger={<Button variant="ghost" size="xs">Change</Button>}
                    />
                  }
                />
                <TicketFollowers ticket={data.ticket} />
              </>
            )}
            {adminControls}
            {agentPanel && <ActivityTimeline activity={agentPanel.activity} />}
            {showOldInvestigate && (
              <InvestigationRailCard
                ticketId={ticketId}
                onUseAsReply={text => setDraftSeed(s => ({ text, v: (s?.v ?? 0) + 1, label: DRAFT_LABEL }))}
              />
            )}
            <ContextPanel ticket={data.ticket} links={data.links} viewingAs={data.viewingAs} requesterShown={Boolean(data.requester)} />
          </aside>
        )}
        {customerRail && (
          <aside
            className="w-[clamp(240px,28%,300px)] shrink-0 overflow-y-auto border-l border-etn-border-hairline bg-background px-5 pb-10 pt-5"
            data-testid="customer-rail"
          >
            <TicketFollowers
              ticket={data.ticket}
              audience="customer"
              canManage={canManageFollowers}
              meId={data.viewingAs ? undefined : data.me._id}
              onLeft={() => navigate({ to: '/tickets' as never })}
            />
          </aside>
        )}
      </div>
    </div>
  )
}
